import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { DataSource, QualityClass } from "@gazelab/shared";
import { QUALITY_LABELS } from "../../lib/format.js";

type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "synthetic";

const TONES: Record<Tone, string> = {
  neutral: "bg-slate-100 text-slate-700 ring-slate-300",
  info: "bg-sky-50 text-sky-800 ring-sky-300",
  success: "bg-emerald-50 text-emerald-800 ring-emerald-300",
  warning: "bg-amber-50 text-amber-900 ring-amber-300",
  danger: "bg-rose-50 text-rose-800 ring-rose-300",
  synthetic: "bg-fuchsia-100 text-fuchsia-900 ring-fuchsia-400",
};

export function Badge({ tone = "neutral", children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONES[tone]}`}>
      {children}
    </span>
  );
}

export function SourceBadge({ source }: { source: DataSource }) {
  return source === "synthetic" ? (
    <Badge tone="synthetic" title="Datos generados por computadora; no provienen de personas">
      SINTÉTICO
    </Badge>
  ) : (
    <Badge tone="info" title="Captura con webcam (WebGazer)">
      Real (webcam)
    </Badge>
  );
}

export function QualityBadge({ value, override }: { value: QualityClass; override?: boolean }) {
  const tone: Tone = value === "pass" ? "success" : value === "warn" ? "warning" : "danger";
  return (
    <span className="inline-flex items-center gap-1">
      <Badge tone={tone}>{QUALITY_LABELS[value]}</Badge>
      {override && <Badge tone="warning" title="Continuó con autorización manual auditada">override</Badge>}
    </span>
  );
}

export function Alert({ tone = "info", title, children }: { tone?: Exclude<Tone, "neutral">; title?: string; children?: ReactNode }) {
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={`rounded-md p-3 text-sm ring-1 ring-inset ${TONES[tone]}`}>
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={title ? "mt-1" : ""}>{children}</div>}
    </div>
  );
}

export function ErrorAlert({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return <Alert tone="danger" title="Error">{error}</Alert>;
}

export function Spinner({ label = "Cargando…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-sky-700" aria-hidden />
      {label}
    </div>
  );
}

export function SyntheticBanner({ children }: { children?: ReactNode }) {
  return (
    <div role="note" className="flex items-start gap-3 rounded-xl bg-fuchsia-50 px-3 py-2 text-sm text-fuchsia-950 ring-1 ring-inset ring-fuchsia-300" data-testid="synthetic-banner">
      <span className="mt-px shrink-0 rounded-md bg-fuchsia-700 px-2 py-0.5 text-xs font-black tracking-widest text-white">SINTÉTICO</span>
      <div className="min-w-0">
        <p>
          <strong>Datos de demostración (SINTÉTICOS):</strong> sesiones generadas por computadora para probar el panel. No provienen de personas reales y{" "}
          <strong>nunca se combinan con resultados reales</strong>.
        </p>
        {children}
      </div>
    </div>
  );
}

export function Modal({ open, title, onClose, children, footer }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      className="m-auto w-[min(92vw,36rem)] rounded-lg p-0 shadow-xl backdrop:bg-slate-900/40"
    >
      {open && (
        <div className="p-5">
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          <div className="mt-3 text-sm text-slate-700">{children}</div>
          <div className="mt-5 flex justify-end gap-2">{footer}</div>
        </div>
      )}
    </dialog>
  );
}

/** Diálogo de confirmación; si `requireText` se define, hay que escribirlo para habilitar el botón. */
export function ConfirmDialog(props: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  requireText?: string;
  withReason?: string;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const inputId = useId();
  useEffect(() => {
    if (props.open) setText("");
  }, [props.open]);
  const blocked =
    (props.requireText !== undefined && text.trim() !== props.requireText) || (props.withReason !== undefined && text.trim().length < 3);
  return (
    <Modal
      open={props.open}
      title={props.title}
      onClose={props.onCancel}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={props.onCancel}>
            Cancelar
          </button>
          <button
            type="button"
            className={props.danger ? "btn-danger" : "btn-primary"}
            disabled={blocked || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await props.onConfirm(text.trim());
              } finally {
                setBusy(false);
              }
            }}
          >
            {props.confirmLabel}
          </button>
        </>
      }
    >
      <div>{props.message}</div>
      {(props.requireText !== undefined || props.withReason !== undefined) && (
        <div className="mt-3">
          <label htmlFor={inputId} className="label">
            {props.requireText !== undefined ? (
              <>
                Escribe <strong>{props.requireText}</strong> para confirmar
              </>
            ) : (
              props.withReason
            )}
          </label>
          <input id={inputId} className="input" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
        </div>
      )}
    </Modal>
  );
}

export function PageHeader({ title, children, description }: { title: string; description?: ReactNode; children?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm text-slate-600">{description}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-200" title={hint}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold">{value}</div>
    </div>
  );
}
